<div class="section page-container">
  <div class="header-row">
    <h2>{{ 'team.title' | translate }}</h2>
  </div>

  <div class="scrollable-content">

    <!-- Active Team Members Section -->
    <div class="section-header">
      <h3>{{ 'team.activeMembers' | translate }}</h3>
      <app-checkbox [(ngModel)]="showInactiveUsers" (ngModelChange)="onShowInactiveChange()"
        [label]="'team.showInactive' | translate" [labelPosition]="'left'" data-testid="show-inactive-toggle" />
    </div>

    <div class="section">
      <!-- Loading/Error states for users -->
      @if (usersLoading()) {
      <div data-testid="users-loading">{{ 'team.loading' | translate }}</div>
      }
      @if (usersError()) {
      <div data-testid="users-error" class="error">{{ usersError() }}</div>
      }

      @if (!usersLoading() && !usersError()) {
      <div class="section-content team-members-list">
        @for (member of users(); track member.id) {
        <div class="member-card" [class.inactive]="member.is_active === 0" [attr.data-testid]="'member-' + member.id">
          <div class="member-info">
            <div class="member-name">
              {{ member.full_name }}
              @if (member.id === currentUserId) {
              <span>{{ 'team.you' | translate }}</span>
              }
              @if (member.is_active === 0) {
              <span class="inactive-badge">{{ 'team.deactivated' | translate }}</span>
              }
            </div>
            <div class="member-details">
              <span class="member-email">{{ member.email }}</span>
              <span class="member-id" style="color: var(--fg-muted); font-size: 0.85em; font-family: monospace;">ID: {{
                member.id }}</span>
              <span class="member-role-badge">{{ member.role }}</span>
            </div>
          </div>
          @if (member.is_active !== 0) {
          <div class="member-actions">
            <app-button variant="outlined" size="sm" (click)="showChangeRoleDialog(member)"
              [attr.data-testid]="'change-role-' + member.id">
              {{ 'team.changeRole' | translate }}
            </app-button>
            <app-button variant="outlined" color="danger" size="sm" (click)="showRemoveMemberDialog(member)"
              [attr.data-testid]="'remove-member-' + member.id">
              {{ member.id === currentUserId ? ('team.leave' | translate) : ('team.remove' | translate) }}
            </app-button>
          </div>
          }
        </div>
        }

        @if (users().length === 0) {
        <div data-testid="no-members">
          {{ 'team.noMembers' | translate }}
        </div>
        }
      </div>
      }
    </div>

    <!-- Pending Invitations Section -->
    <div class="section-header">
      <h3>{{ 'team.invitations-form' | translate }}</h3>
    </div>
    <div class="section">
      <!-- Create invitation form -->
      <div class="section-content create-form" data-testid="create-invitation-form">
        <p class="info-message">{{ 'team.infoMessage' | translate }}</p>
        <div class="form-row">
          <div>
            <div class="label">Email</div>
            <app-input>
              <input type="email" [(ngModel)]="email" [placeholder]="'team.emailPlaceholder' | translate"
                [disabled]="creating" data-testid="email-input" class="input" />
            </app-input>
          </div>
          <div class="flex-column">
            <div class="label">Role</div>
            <app-select>
              <select [(ngModel)]="role" [disabled]="creating" data-testid="role-select">
                <option value="user">{{ 'team.roleUser' | translate }}</option>
                <option value="admin">{{ 'team.roleAdmin' | translate }}</option>
              </select>
            </app-select>
          </div>
          <div class="flex-grow"></div>
          <app-button (click)="createInvitation()" [disabled]="!email || creating" data-testid="create-button">
            {{ creating ? ('team.creating' | translate) : ('team.sendInvitation' | translate) }}
          </app-button>
        </div>
      </div>

      <!-- Loading/Error states -->
      @if (loading()) {
      <div data-testid="loading">{{ 'team.loadingInvitations' | translate }}</div>
      }
      @if (error()) {
      <div data-testid="error" class="error">{{ error() }}</div>
      }

      <div class="section-header">
        <h3>{{ 'team.pendingInvitations' | translate }}</h3>
      </div>
      <!-- Invitations list -->
      @if (!loading() && !error()) {
      <div class="invitations-list">
        @for (invitation of displayedInvitations; track invitation.id) {
        <div class="invitation-card" [attr.data-testid]="'invitation-' + invitation.id">
          <div class="invitation-header">
            <span class="email">{{ invitation.data.email }}</span>
            <span class="status" [class.pending]="invitation.displayStatus === 'pending'"
              [class.accepted]="invitation.displayStatus === 'accepted'"
              [class.cancelled]="invitation.displayStatus === 'cancelled'"
              [class.expired]="invitation.displayStatus === 'expired'">
              {{ invitation.displayStatus }}
            </span>
          </div>

          <div class="invitation-details">
            <span>Role: {{ invitation.data.role }}</span>
            <span>{{ 'team.expires' | translate:{ date: (invitation.data.expires_at | date:'short') || '' } }}</span>
            <span style="color: var(--fg-muted); font-size: 0.85em; font-family: monospace;">ID: {{ invitation.id
              }}</span>
          </div>

          @if (invitation.displayStatus === 'pending') {
          <div class="invitation-actions">
            <app-copy-button [text]="getInvitationUrl(invitation.token)" [label]="'team.copyLink' | translate"
              [testId]="'copy-button-' + invitation.id" />
            <button (click)="showCancelConfirmation(invitation)" class="cancel-button"
              [attr.data-testid]="'cancel-button-' + invitation.id">
              {{ 'team.cancel' | translate }}
            </button>
          </div>
          }
        </div>
        }

        @if (hasMoreInvitations) {
        <div class="show-more-link" data-testid="show-more-link">
          <a (click)="showMore()" (keydown.enter)="showMore()" (keydown.space)="showMore()" tabindex="0"
            role="button">...more</a>
        </div>
        }

        @if (invitations().length === 0) {
        <div data-testid="empty-state">
          {{ 'team.noInvitations' | translate }}
        </div>
        }
      </div>
      }
    </div>
  </div>


  <!-- Error Message -->
  @if (actionError) {
  <div class="error-banner" data-testid="error-banner">
    <app-icon name="alert-circle" />
    <span>{{ actionError }}</span>
    <app-button variant="icon" (click)="closeErrorMessage()">
      <app-icon name="x" />
    </app-button>
  </div>
  }

  <!-- Cancel Invitation Modal -->
  @if (showCancelModal && invitationToCancel) {
  <app-modal-dialog (backdropClick)="closeCancelModal()">
    <div header>
      <div class="modal-title-row">
        <div class="heading-md">{{ 'team.cancelModal.title' | translate }}</div>
        <app-button variant="icon" (click)="closeCancelModal()">
          <app-icon name="x" />
        </app-button>
      </div>
    </div>

    <div content>
      <p>{{ 'team.cancelModal.message' | translate:{ email: invitationToCancel.email } }}</p>
      <p>{{ 'team.cancelModal.warning' | translate }}</p>
    </div>

    <div footer>
      <app-button variant="text" (click)="closeCancelModal()">
        {{ 'team.cancelModal.keep' | translate }}
      </app-button>
      <app-button color="danger" (click)="confirmCancel()">
        {{ 'team.cancelModal.confirm' | translate }}
      </app-button>
    </div>
  </app-modal-dialog>
  }

  <!-- Remove/Leave Confirmation Modal -->
  @if (showRemoveModal) {
  <app-modal-dialog (backdropClick)="cancelRemove()">
    <div header>
      <div class="modal-title-row">
        <div class="heading-md">{{ userToRemove?.isCurrentUser ? ('team.removeModal.titleLeave' | translate) :
          ('team.removeModal.titleRemove' | translate) }}</div>
        <app-button variant="icon" (click)="cancelRemove()">
          <app-icon name="x" />
        </app-button>
      </div>
    </div>

    <div content>
      @if (userToRemove?.isCurrentUser) {
      <p>
        {{ 'team.removeModal.messageLeave' | translate }}
      </p>
      } @else {
      <p>
        {{ 'team.removeModal.messageRemove' | translate:{ name: userToRemove?.name || '' } }}
      </p>
      }
    </div>

    <div footer>
      <app-button variant="text" (click)="cancelRemove()">
        {{ 'team.removeModal.cancel' | translate }}
      </app-button>
      <app-button color="danger" (click)="confirmRemoveMember()">
        {{ userToRemove?.isCurrentUser ? ('team.leave' | translate) : ('team.remove' | translate) }}
      </app-button>
    </div>
  </app-modal-dialog>
  }

  <!-- Not Implemented Modal -->
  @if (showNotImplementedModal) {
  <app-modal-dialog (backdropClick)="closeNotImplementedModal()">
    <div header>
      <div class="modal-title-row">
        <div class="heading-md">{{ 'team.notImplementedModal.title' | translate }}</div>
        <app-button variant="icon" (click)="closeNotImplementedModal()">
          <app-icon name="x" />
        </app-button>
      </div>
    </div>

    <div content>
      <p>{{ 'team.notImplementedModal.message1' | translate }}</p>
      <p>{{ 'team.notImplementedModal.message2' | translate }}</p>
    </div>

    <div footer>
      <app-button (click)="closeNotImplementedModal()">
        {{ 'team.notImplementedModal.ok' | translate }}
      </app-button>
    </div>
  </app-modal-dialog>
  }
</div>